/**
 * Statistik.js – Shop-Statistik: MHD + Backshop Abschriften + Rentabilität
 * KPIs · Charts · Top-10 · Detail · PDF + Excel Export · Rentabilitäts-Analyse
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DashboardLayout } from '../../components/DashboardLayout';
import { EmployeeStatTab } from '../../components/EmployeeStatTab';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import {
  BarChart3, TrendingUp, TrendingDown, Minus, Package, Calendar,
  Users, Download, FileSpreadsheet, Loader2, ChevronDown, AlertTriangle,
  Info, RefreshCw, Settings, CheckCircle2, XCircle, PenLine, Truck,
  ShoppingBag, Euro, Gift,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Heute' },
  { value: 'week', label: 'Diese Woche' },
  { value: 'month', label: 'Dieser Monat' },
  { value: 'last_month', label: 'Letzter Monat' },
  { value: 'custom', label: 'Eigener Zeitraum' },
];

const TAB_CONFIG = {
  mhd: {
    label: 'MHD-Abschriften',
    color: '#ef4444',
    lightColor: '#fef2f2',
    description: 'Abgelaufene Produkte aus dem Regal (Lion, Snickers, etc.)',
  },
  backshop: {
    label: 'Backshop-Abschriften',
    color: '#f97316',
    lightColor: '#fff7ed',
    description: 'Aufgebackene Waren, die nicht verkauft wurden (Brötchen, Croissants, etc.)',
  },
  rentabilitaet: {
    label: 'Rentabilität',
    color: '#7c3aed',
    lightColor: '#f5f3ff',
    description: 'Lieferung vs. Abschrift – Verlustquote pro Artikel',
  },
  shift: {
    label: 'Schichtabrechnung',
    color: '#0ea5e9',
    lightColor: '#f0f9ff',
    description: 'Kassendifferenzen & Lohnabzüge je Mitarbeiter – wer rechnet zuverlässig ab?',
  },
};

// Generische Config für den Mitarbeiter-Statistik-Tab "Schichtabrechnung".
// Späteres Muster: neue Kategorie = neues Endpoint + neue Config dieser Form.
const SHIFT_STAT_CONFIG = {
  accentColor: '#0ea5e9',
  kpis: [
    { key: 'shifts', label: 'Schichten', icon: Package, color: '#0ea5e9' },
    { key: 'avg_abs_diff', label: 'Ø Differenz', icon: Euro, color: '#f97316', format: 'euro' },
    { key: 'passt_pct', label: 'Passt genau', icon: CheckCircle2, color: '#059669', format: 'pct' },
    { key: 'abzug_count', label: 'Lohnabzüge', icon: PenLine, color: '#6366f1' },
    { key: 'abzug_sum', label: 'Summe Abzüge', icon: Euro, color: '#ef4444', format: 'euro' },
  ],
  columns: [
    { key: 'name', label: 'Mitarbeiter', align: 'left', sortable: true },
    { key: 'shifts', label: 'Schichten', align: 'right', sortable: true },
    { key: 'avg_abs_diff', label: 'Ø Differenz', align: 'right', sortable: true, format: 'euro' },
    { key: 'passt_pct', label: 'Passt genau', align: 'right', sortable: true, format: 'pct' },
    { key: 'abzug_count', label: 'Lohnabzüge', align: 'right', sortable: true },
    { key: 'abzug_sum', label: 'Summe Abzüge', align: 'right', sortable: true, format: 'euro' },
  ],
  defaultSortKey: 'shifts',
  chart: { valueKey: 'avg_abs_diff', valueLabel: 'Ø Differenz', unit: ' €' },
};

const EMP_STATUS_OPTIONS = [
  { value: 'active', label: 'Nur aktive' },
  { value: 'inactive', label: 'Nur inaktive' },
  { value: 'all', label: 'Alle' },
];

const WEEKDAY_COLORS = ['#3b82f6', '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f97316', '#eab308'];

// ── Hilfskomponenten ──────────────────────────────────────────────────────────

function KpiCard({ icon: Icon, label, value, sub, trend, color = '#3b82f6' }) {
  const trendUp = trend > 0;
  const trendDown = trend < 0;
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground font-medium truncate">{label}</p>
            <p className="text-2xl font-bold mt-1 text-foreground">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5 truncate">{sub}</p>}
          </div>
          <div className="p-2 rounded-xl shrink-0 ml-2" style={{ backgroundColor: `${color}15` }}>
            <Icon className="h-5 w-5" style={{ color }} />
          </div>
        </div>
        {trend !== undefined && trend !== null && (
          <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${
            trendUp ? 'text-red-600' : trendDown ? 'text-green-600' : 'text-muted-foreground'
          }`}>
            {trendUp ? <TrendingUp className="h-3 w-3" /> :
             trendDown ? <TrendingDown className="h-3 w-3" /> :
             <Minus className="h-3 w-3" />}
            <span>
              {trendUp ? '+' : ''}{trend}% vs. Vorperiode
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SectionHeader({ title, icon: Icon, color = '#1e3a8a' }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="p-1.5 rounded-lg" style={{ backgroundColor: `${color}15` }}>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <h3 className="font-semibold text-sm text-foreground">{title}</h3>
    </div>
  );
}

// ── Hauptseite ────────────────────────────────────────────────────────────────

export const Statistik = () => {
  const [activeTab, setActiveTab] = useState('mhd');
  const [period, setPeriod] = useState('month');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [loading, setLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [data, setData] = useState(null);
  const [showDetailTable, setShowDetailTable] = useState(false);

  // Schichtabrechnung-Tab (Mitarbeiter-Statistik)
  const [empStatus, setEmpStatus] = useState('active');
  const [shiftData, setShiftData] = useState(null);

  // Rentabilitäts-States
  const [rentDays, setRentDays] = useState(90);
  const [rentDateFrom, setRentDateFrom] = useState('');
  const [rentDateTo, setRentDateTo] = useState('');
  const [rentThreshold, setRentThreshold] = useState(20);
  const [showThresholdEdit, setShowThresholdEdit] = useState(false);
  const [rentLoading, setRentLoading] = useState(false);
  const [rentData, setRentData] = useState(null);
  const [eanInputs, setEanInputs] = useState({});
  const [savingEan, setSavingEan] = useState({});
  const [showBackshopSection, setShowBackshopSection] = useState(false);
  const [rentSearch, setRentSearch] = useState('');

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const tabCfg = TAB_CONFIG[activeTab];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'shift') {
        const params = { period, emp_status: empStatus };
        if (period === 'custom' && dateFrom && dateTo) {
          params.date_from = dateFrom;
          params.date_to = dateTo;
        }
        if (employeeId) params.employee_id = employeeId;
        const res = await axios.get(`${API}/api/statistics/shift-settlement`, { headers, params });
        setShiftData(res.data);
        return;
      }
      const params = { tab: activeTab, period };
      if (period === 'custom' && dateFrom && dateTo) {
        params.date_from = dateFrom;
        params.date_to = dateTo;
      }
      if (employeeId) params.employee_id = employeeId;

      const res = await axios.get(`${API}/api/statistics/shop-writeoffs`, { headers, params });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Laden der Statistik');
    } finally {
      setLoading(false);
    }
  }, [activeTab, period, dateFrom, dateTo, employeeId, empStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Rentabilität laden
  const fetchRentabilitaet = useCallback(async () => {
    setRentLoading(true);
    try {
      const params = { days: rentDays, threshold: rentThreshold };
      if (rentDays === 0 && rentDateFrom && rentDateTo) {
        params.date_from = rentDateFrom;
        params.date_to = rentDateTo;
        delete params.days;
      }
      const res = await axios.get(`${API}/api/statistics/rentabilitaet`, { headers, params });
      setRentData(res.data);
      setRentSearch('');  // Suche zurücksetzen bei neuem Load
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Laden der Rentabilität');
    } finally {
      setRentLoading(false);
    }
  }, [rentDays, rentDateFrom, rentDateTo, rentThreshold]);

  useEffect(() => {
    if (activeTab === 'rentabilitaet') fetchRentabilitaet();
  }, [activeTab, fetchRentabilitaet]);

  // EAN speichern
  const handleSaveEan = async (item) => {
    const key = item.fremd_article_number || item.article_name;
    const ean = (eanInputs[key] || '').trim();
    if (!ean) { toast.error('Bitte EAN eingeben'); return; }
    if (!/^\d{8,14}$/.test(ean)) { toast.error('EAN muss 8–14 Ziffern haben'); return; }

    setSavingEan(s => ({ ...s, [key]: true }));
    try {
      await axios.post(`${API}/api/statistics/save-ean-mapping`, {
        ean,
        fremd_article_number: item.fremd_article_number,
        article_name: item.article_name,
      }, { headers });
      toast.success(`EAN ${ean} gespeichert – Artikel wird ab sofort erkannt`);
      setEanInputs(e => ({ ...e, [key]: '' }));
      fetchRentabilitaet();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingEan(s => ({ ...s, [key]: false }));
    }
  };

  // Tab-Wechsel → Employee-Filter zurücksetzen
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setEmployeeId('');
    if (tab !== 'rentabilitaet' && tab !== 'shift') setData(null);
    if (tab === 'shift') setShiftData(null);
  };

  const handleEmpStatusChange = (val) => {
    setEmpStatus(val);
    setEmployeeId('');  // Auswahl könnte im neuen Filter fehlen
  };

  // Excel-Export (Client-seitig als CSV)
  const handleExcelExport = () => {
    if (activeTab === 'shift') {
      if (!shiftData) return;
      const rows = [];
      if (shiftData.mode === 'all') {
        rows.push(['Mitarbeiter', 'Aktiv', 'Schichten', 'Ø Differenz (€)', 'Passt genau (%)', 'Lohnabzüge', 'Summe Abzüge (€)']);
        (shiftData.comparison || []).forEach(r => {
          rows.push([r.name, r.active ? 'aktiv' : 'inaktiv', r.shifts, r.avg_abs_diff, r.passt_pct, r.abzug_count, r.abzug_sum]);
        });
      } else {
        rows.push([`Mitarbeiter: ${shiftData.employee_name || ''}`]);
        rows.push(['Wochentag', 'Ø Differenz (€)', 'Schichten']);
        (shiftData.weekday_chart || []).forEach(d => rows.push([d.day, d.avg_abs_diff, d.count]));
      }
      const csvContent = rows.map(r => r.map(c => `"${c}"`).join(';')).join('\n');
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `statistik_schichtabrechnung_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      toast.success('Excel-Export heruntergeladen');
      return;
    }
    if (!data) return;
    const rows = [['Artikel', 'Stück', 'Anzahl Abschr.', 'Anteil %']];
    (data.top_articles || []).forEach(a => {
      rows.push([a.name, a.quantity, a.count, `${a.pct_of_total}%`]);
    });
    rows.push([]);
    rows.push(['Wochentag', 'Stück', 'Abschr.']);
    (data.weekday_chart || []).forEach(d => {
      rows.push([d.day, d.quantity, d.count]);
    });
    rows.push([]);
    rows.push(['Mitarbeiter', 'Abschr.', 'Stück']);
    (data.by_employee || []).forEach(e => {
      rows.push([e.name, e.count, e.quantity]);
    });

    const csvContent = rows.map(r => r.map(c => `"${c}"`).join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `statistik_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Excel-Export heruntergeladen');
  };

  // PDF-Export (Backend)
  const handlePdfExport = async () => {
    setExportingPdf(true);
    try {
      if (activeTab === 'shift') {
        const params = { period, emp_status: empStatus };
        if (period === 'custom' && dateFrom && dateTo) {
          params.date_from = dateFrom;
          params.date_to = dateTo;
        }
        if (employeeId) params.employee_id = employeeId;
        const res = await axios.get(`${API}/api/statistics/shift-settlement/pdf`, {
          headers, params, responseType: 'blob',
        });
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `statistik_schichtabrechnung_${new Date().toISOString().split('T')[0]}.pdf`;
        a.click();
        toast.success('PDF exportiert');
        return;
      }
      const params = { tab: activeTab, period };
      if (period === 'custom' && dateFrom && dateTo) {
        params.date_from = dateFrom;
        params.date_to = dateTo;
      }
      if (employeeId) params.employee_id = employeeId;

      const res = await axios.get(`${API}/api/statistics/shop-writeoffs/pdf`, {
        headers, params, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `statistik_${activeTab}_${new Date().toISOString().split('T')[0]}.pdf`;
      a.click();
      toast.success('PDF exportiert');
    } catch {
      toast.error('PDF-Export fehlgeschlagen');
    } finally {
      setExportingPdf(false);
    }
  };

  const kpis = data?.kpis || {};
  const topArticles = data?.top_articles || [];
  const weeklyTrend = data?.weekly_trend || [];
  const weekdayChart = data?.weekday_chart || [];
  const byEmployee = data?.by_employee || [];
  const employees = data?.employees || [];
  const detail = data?.detail || [];

  // Für Shift-Tab: eigene Mitarbeiterliste + Export-Verfügbarkeit
  const currentEmployees = activeTab === 'shift' ? (shiftData?.employees || []) : employees;
  const canExport = activeTab === 'shift' ? !!shiftData : !!data;

  // Worst weekday highlight
  const maxQtyDay = Math.max(...weekdayChart.map(d => d.quantity), 0);

  return (
    <DashboardLayout>
      <div className="space-y-5 pb-8 px-1 sm:px-0">

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              <BarChart3 className="h-7 w-7 text-blue-600" />
              Statistik
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Shop-Auswertungen für Ihren Betrieb
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm" onClick={handleExcelExport}
              disabled={loading || !canExport}
              className="gap-1.5 text-xs"
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="h-3.5 w-3.5 text-green-600" />
              Excel
            </Button>
            <Button
              variant="outline" size="sm" onClick={handlePdfExport}
              disabled={exportingPdf || loading || !canExport}
              className="gap-1.5 text-xs"
              data-testid="export-pdf-btn"
            >
              {exportingPdf
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Download className="h-3.5 w-3.5 text-blue-600" />}
              PDF
            </Button>
          </div>
        </div>

        {/* ── Tabs ───────────────────────────────────────────────────── */}
        <div className="flex gap-1 border-b border-border">
          {Object.entries(TAB_CONFIG).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => handleTabChange(key)}
              data-testid={`tab-${key}`}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {cfg.label}
            </button>
          ))}
        </div>

        {/* ── Filter-Zeile ────────────────────────────────────────────── */}
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-wrap gap-2 items-center">
              {/* Zeitraum */}
              <div className="flex gap-1 flex-wrap">
                {PERIOD_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setPeriod(opt.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      period === opt.value
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-background border-border text-muted-foreground hover:border-blue-400'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {period === 'custom' && (
                <div className="flex gap-2 items-center">
                  <input
                    type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                    className="h-8 px-2 rounded-lg border text-xs bg-background"
                  />
                  <span className="text-xs text-muted-foreground">–</span>
                  <input
                    type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                    className="h-8 px-2 rounded-lg border text-xs bg-background"
                  />
                </div>
              )}

              {/* Aktiv/Inaktiv-Filter (Mitarbeiter-Statistik-Tabs) */}
              {activeTab === 'shift' && (
                <div className="flex items-center gap-1 ml-auto">
                  {EMP_STATUS_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => handleEmpStatusChange(opt.value)}
                      data-testid={`emp-status-${opt.value}`}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        empStatus === opt.value
                          ? 'bg-sky-600 text-white border-sky-600'
                          : 'bg-background border-border text-muted-foreground hover:border-sky-400'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Mitarbeiter-Filter */}
              {currentEmployees.length > 0 && (
                <div className={`flex items-center gap-1.5 ${activeTab === 'shift' ? '' : 'ml-auto'}`}>
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                  <select
                    value={employeeId}
                    onChange={e => setEmployeeId(e.target.value)}
                    className="h-8 px-2 rounded-lg border text-xs bg-background"
                    data-testid="employee-filter"
                  >
                    <option value="">Alle Mitarbeiter</option>
                    {currentEmployees.map(e => (
                      <option key={e.id} value={e.id}>
                        {e.name}{e.active === false ? ' (inaktiv)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Refresh */}
              <button
                onClick={fetchData}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                title="Aktualisieren"
              >
                <RefreshCw className={`h-3.5 w-3.5 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Tab-Beschreibung */}
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
              <Info className="h-3 w-3" />{tabCfg.description}
            </p>
          </CardContent>
        </Card>

        {/* ── Loading oder MHD/Backshop Inhalt ───────────────────── */}
        {(activeTab === 'mhd' || activeTab === 'backshop') && (
        loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-blue-600 mb-3" />
            <p className="text-sm text-muted-foreground">Daten werden geladen…</p>
          </div>
        ) : (
          <>
            {/* ── KPI-Karten ──────────────────────────────────────────── */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <KpiCard
                icon={Package}
                label="Abschriften gesamt"
                value={kpis.total_count ?? 0}
                sub={`${kpis.total_quantity ?? 0} Stück insgesamt`}
                color="#ef4444"
              />
              <KpiCard
                icon={BarChart3}
                label="Trend vs. Vorperiode"
                value={`${kpis.trend_pct > 0 ? '+' : ''}${kpis.trend_pct ?? 0}%`}
                sub={`Vorperiode: ${kpis.prev_count ?? 0} Abschr.`}
                trend={kpis.trend_pct}
                color="#6366f1"
              />
              <KpiCard
                icon={TrendingUp}
                label="Häufigster Artikel"
                value={(kpis.top_article ?? '–').slice(0, 20)}
                sub="mit den meisten Stück"
                color={tabCfg.color}
              />
              <KpiCard
                icon={Calendar}
                label="Ø pro Woche"
                value={kpis.avg_per_week ?? 0}
                sub="Abschr. (Ø letzte 4 Wochen)"
                color="#059669"
              />
            </div>

            {/* ── Charts ──────────────────────────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

              {/* Wochentrend */}
              <Card>
                <CardContent className="p-4">
                  <SectionHeader title="Wochentrend (letzte 8 Wochen)" icon={TrendingUp} color="#3b82f6" />
                  {weeklyTrend.some(w => w.count > 0) ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={weeklyTrend} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                        <Tooltip
                          formatter={(val, name) => [val, name === 'quantity' ? 'Stück' : 'Abschr.']}
                          labelFormatter={l => `${l}`}
                          contentStyle={{ fontSize: 11, borderRadius: 8 }}
                        />
                        <Bar dataKey="quantity" name="quantity" fill={tabCfg.color} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[200px] flex items-center justify-center">
                      <p className="text-sm text-muted-foreground">Keine Daten im Zeitraum</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Wochentags-Analyse */}
              <Card>
                <CardContent className="p-4">
                  <SectionHeader title="Wochentags-Analyse" icon={Calendar} color="#f97316" />
                  <p className="text-xs text-muted-foreground mb-3">
                    An welchen Tagen fallen die meisten Abschriften an?
                  </p>
                  {weekdayChart.some(d => d.count > 0) ? (
                    <ResponsiveContainer width="100%" height={190}>
                      <BarChart data={weekdayChart} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="short" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                        <Tooltip
                          formatter={(val) => [val, 'Stück']}
                          labelFormatter={(l) => {
                            const d = weekdayChart.find(x => x.short === l);
                            return d?.day || l;
                          }}
                          contentStyle={{ fontSize: 11, borderRadius: 8 }}
                        />
                        <Bar dataKey="quantity" radius={[4, 4, 0, 0]}>
                          {weekdayChart.map((d, i) => (
                            <Cell
                              key={d.short}
                              fill={d.quantity === maxQtyDay && maxQtyDay > 0 ? '#ef4444' :
                                    d.quantity >= maxQtyDay * 0.8 && maxQtyDay > 0 ? '#f97316' :
                                    WEEKDAY_COLORS[i % WEEKDAY_COLORS.length]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[190px] flex items-center justify-center">
                      <p className="text-sm text-muted-foreground">Keine Daten im Zeitraum</p>
                    </div>
                  )}
                  {maxQtyDay > 0 && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Schlechtester Tag: <strong>{weekdayChart.find(d => d.quantity === maxQtyDay)?.day}</strong>
                      &nbsp;({maxQtyDay} Stück) – Bestellmenge prüfen!
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* ── Top-10 + Mitarbeiter ────────────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

              {/* Top-10 Artikel */}
              <Card>
                <CardContent className="p-4">
                  <SectionHeader title="Top 10 Verlust-Artikel" icon={AlertTriangle} color="#ef4444" />
                  {topArticles.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">Keine Daten im Zeitraum</p>
                  ) : (
                    <div className="space-y-1.5">
                      {topArticles.map((art, i) => (
                        <div key={art.name} className={`flex items-center gap-2 p-2 rounded-lg ${
                          i === 0 ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' :
                          i === 1 ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-100' :
                          'bg-muted/30'
                        }`}>
                          <span className={`text-xs font-bold w-5 text-center shrink-0 ${
                            i === 0 ? 'text-red-600' : i === 1 ? 'text-orange-600' : 'text-muted-foreground'
                          }`}>{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate">{art.name}</p>
                            {activeTab === 'backshop' && art.category && (
                              <p className="text-xs text-muted-foreground">{art.category}</p>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-sm font-bold">{art.quantity} Stk.</p>
                            <p className="text-xs text-muted-foreground">{art.pct_of_total}% Anteil</p>
                          </div>
                          {/* Mini Progress Bar */}
                          <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden shrink-0">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${art.pct_of_total}%`,
                                backgroundColor: i === 0 ? '#ef4444' : i === 1 ? '#f97316' : tabCfg.color,
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Nach Mitarbeiter */}
              <Card>
                <CardContent className="p-4">
                  <SectionHeader title="Abschriften nach Mitarbeiter" icon={Users} color="#6366f1" />
                  {byEmployee.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">Keine Daten im Zeitraum</p>
                  ) : (
                    <div className="space-y-2">
                      {byEmployee.slice(0, 8).map(emp => {
                        const maxCnt = byEmployee[0]?.count || 1;
                        const pct = Math.round(emp.count / (kpis.total_count || 1) * 100);
                        return (
                          <div key={emp.name} className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0" />
                            <span className="text-xs flex-1 truncate">{emp.name}</span>
                            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden max-w-[80px]">
                              <div
                                className="h-full bg-indigo-500 rounded-full transition-all"
                                style={{ width: `${(emp.count / maxCnt) * 100}%` }}
                              />
                            </div>
                            <span className="text-xs font-semibold w-10 text-right">{emp.count}</span>
                            <span className="text-xs text-muted-foreground w-8 text-right">{pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* ── Detail-Tabelle (aufklappbar) ─────────────────────────── */}
            <Card>
              <CardContent className="p-4">
                <button
                  onClick={() => setShowDetailTable(v => !v)}
                  className="flex items-center justify-between w-full"
                  data-testid="toggle-detail-table"
                >
                  <SectionHeader title={`Detailtabelle (${detail.length} Einträge)`} icon={BarChart3} color="#64748b" />
                  <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showDetailTable ? 'rotate-180' : ''}`} />
                </button>
                {showDetailTable && (
                  <div className="mt-3 overflow-x-auto">
                    {detail.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">Keine Einträge</p>
                    ) : (
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="bg-muted/50">
                            <th className="text-left p-2 border-b font-semibold">Artikel</th>
                            {activeTab === 'backshop' && <th className="text-left p-2 border-b font-semibold">Kategorie</th>}
                            {activeTab === 'mhd' && <th className="text-left p-2 border-b font-semibold">MHD</th>}
                            <th className="text-right p-2 border-b font-semibold">Stück</th>
                            <th className="text-left p-2 border-b font-semibold">Datum</th>
                            <th className="text-left p-2 border-b font-semibold">Mitarbeiter</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.map((row, i) => (
                            <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                              <td className="p-2 border-b max-w-[200px] truncate">{row.product_name}</td>
                              {activeTab === 'backshop' && <td className="p-2 border-b text-muted-foreground">{row.category || '–'}</td>}
                              {activeTab === 'mhd' && <td className="p-2 border-b text-muted-foreground">{row.expiry_date || '–'}</td>}
                              <td className="p-2 border-b text-right font-medium">{row.quantity}</td>
                              <td className="p-2 border-b text-muted-foreground">{row.timestamp_fmt}</td>
                              <td className="p-2 border-b text-muted-foreground">{row.employee}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ── TGTG-Statistik (nur im Backshop-Tab) ─────────────────── */}
            {activeTab === 'backshop' && data?.tgtg && (
              <>
                {/* Kombiniertes Gesamt-Banner */}
                <Card className="border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Gift className="h-5 w-5 text-green-600" />
                      <h3 className="font-semibold text-sm text-green-800 dark:text-green-300">
                        Backshop + TGTG Gesamtübersicht
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm">
                        <p className="text-xs text-red-600 font-medium">Abgeschrieben</p>
                        <p className="text-xl font-bold text-red-700">{kpis.total_quantity ?? 0} Stk.</p>
                        <p className="text-xs text-muted-foreground">{kpis.total_count ?? 0} Vorgänge</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm">
                        <p className="text-xs text-green-600 font-medium">TGTG gerettet</p>
                        <p className="text-xl font-bold text-green-700">{data.tgtg.total_bags} Beutel</p>
                        <p className="text-xs text-muted-foreground">{data.tgtg.total_orders} Bestellungen</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm">
                        <p className="text-xs text-blue-600 font-medium">TGTG Einnahmen</p>
                        <p className="text-xl font-bold text-blue-700">{data.tgtg.total_revenue.toFixed(2)} €</p>
                        <p className="text-xs text-muted-foreground">im Zeitraum</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm">
                        <p className="text-xs text-muted-foreground font-medium">Netto-Verlust</p>
                        <p className="text-xl font-bold">{Math.max(0, (kpis.total_quantity ?? 0) - data.tgtg.total_bags)} Stk.</p>
                        <p className="text-xs text-muted-foreground">Abschr. minus TGTG</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* TGTG Detail-Statistik */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* TGTG Wochentrend */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="p-1.5 rounded-lg bg-green-100 dark:bg-green-900/30">
                          <ShoppingBag className="h-4 w-4 text-green-600" />
                        </div>
                        <h3 className="font-semibold text-sm">TGTG – Wochentrend (letzte 8 Wochen)</h3>
                      </div>
                      {data.tgtg.weekly_trend?.some(w => w.bags > 0) ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={data.tgtg.weekly_trend} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                            <Tooltip
                              formatter={(val, name) => [
                                name === 'bags' ? `${val} Beutel` : `${val.toFixed(2)} €`,
                                name === 'bags' ? 'Beutel' : 'Einnahmen'
                              ]}
                              contentStyle={{ fontSize: 11, borderRadius: 8 }}
                            />
                            <Bar dataKey="bags" fill="#16a34a" radius={[4, 4, 0, 0]} name="bags" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[200px] flex items-center justify-center">
                          <p className="text-sm text-muted-foreground">Keine TGTG-Daten im Zeitraum</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* TGTG Wochentags-Analyse */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1.5 rounded-lg bg-green-100 dark:bg-green-900/30">
                          <Calendar className="h-4 w-4 text-green-600" />
                        </div>
                        <h3 className="font-semibold text-sm">TGTG – Wochentags-Analyse</h3>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">An welchen Tagen werden die meisten Beutel abgeholt?</p>
                      {data.tgtg.weekday_chart?.some(d => d.bags > 0) ? (
                        <div className="space-y-1.5">
                          {data.tgtg.weekday_chart.map(d => {
                            const maxBags = Math.max(...data.tgtg.weekday_chart.map(x => x.bags), 1);
                            return (
                              <div key={d.day} className="flex items-center gap-2">
                                <span className="text-xs w-8 text-muted-foreground shrink-0">{d.short}</span>
                                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-green-500 rounded-full"
                                    style={{ width: `${(d.bags / maxBags) * 100}%` }}
                                  />
                                </div>
                                <span className="text-xs font-medium w-12 text-right">{d.bags} Btl.</span>
                                <span className="text-xs text-green-600 w-14 text-right">{d.revenue.toFixed(2)}€</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="h-[160px] flex items-center justify-center">
                          <p className="text-sm text-muted-foreground">Keine TGTG-Daten im Zeitraum</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* TGTG KPI-Karten */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground font-medium">Bestellungen gesamt</p>
                      <p className="text-2xl font-bold mt-1">{data.tgtg.total_orders}</p>
                      <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${data.tgtg.trend_pct > 0 ? 'text-green-600' : data.tgtg.trend_pct < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                        {data.tgtg.trend_pct > 0 ? <TrendingUp className="h-3 w-3" /> : data.tgtg.trend_pct < 0 ? <TrendingDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                        {data.tgtg.trend_pct > 0 ? '+' : ''}{data.tgtg.trend_pct}% vs. Vorperiode
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground font-medium">Beutel total</p>
                      <p className="text-2xl font-bold mt-1 text-green-700">{data.tgtg.total_bags}</p>
                      <p className="text-xs text-muted-foreground">Ø {data.tgtg.avg_bags_per_week}/Woche</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground font-medium">Einnahmen</p>
                      <p className="text-2xl font-bold mt-1 text-blue-700">{data.tgtg.total_revenue.toFixed(2)} €</p>
                      <p className="text-xs text-muted-foreground">im Zeitraum</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground font-medium">Abgeholt</p>
                      <p className="text-2xl font-bold mt-1">{data.tgtg.abgeholt_count}</p>
                      <p className="text-xs text-amber-600">{data.tgtg.pending_count} noch offen</p>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </>
        )
        )}

        {/* ── SCHICHTABRECHNUNG TAB (Mitarbeiter-Statistik) ────────── */}
        {activeTab === 'shift' && (
          <EmployeeStatTab data={shiftData} loading={loading} config={SHIFT_STAT_CONFIG} />
        )}


        {/* ── RENTABILITÄT TAB ─────────────────────────────────────── */}
        {activeTab === 'rentabilitaet' && (
          <>
            {/* Rentabilität Filter */}
            <Card>
              <CardContent className="p-3">
                <div className="flex flex-wrap gap-2 items-center">
                  {/* Zeitfenster */}
                  {[
                    { days: 30, label: '30 Tage' },
                    { days: 60, label: '60 Tage' },
                    { days: 90, label: '90 Tage' },
                    { days: 0, label: 'Eigener Zeitraum' },
                  ].map(opt => (
                    <button key={opt.days}
                      onClick={() => setRentDays(opt.days)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        rentDays === opt.days
                          ? 'bg-violet-600 text-white border-violet-600'
                          : 'bg-background border-border text-muted-foreground hover:border-violet-400'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}

                  {rentDays === 0 && (
                    <div className="flex gap-2 items-center">
                      <input type="date" value={rentDateFrom} onChange={e => setRentDateFrom(e.target.value)}
                        className="h-8 px-2 rounded-lg border text-xs bg-background" />
                      <span className="text-xs text-muted-foreground">–</span>
                      <input type="date" value={rentDateTo} onChange={e => setRentDateTo(e.target.value)}
                        className="h-8 px-2 rounded-lg border text-xs bg-background" />
                    </div>
                  )}

                  {/* Schwellwert-Einstellung */}
                  <div className="flex items-center gap-1.5 ml-auto">
                    <button
                      onClick={() => setShowThresholdEdit(v => !v)}
                      className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                      title="Verlust-Schwellwert einstellen"
                      data-testid="threshold-settings-btn"
                    >
                      <Settings className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                    {showThresholdEdit && (
                      <div className="flex items-center gap-1 bg-muted/50 rounded-lg px-2 py-1">
                        <span className="text-xs text-muted-foreground">Rot ab</span>
                        <input
                          type="number" min={1} max={100} value={rentThreshold}
                          onChange={e => setRentThreshold(Number(e.target.value))}
                          className="w-14 h-6 px-1 rounded border text-xs text-center bg-background"
                          data-testid="threshold-input"
                        />
                        <span className="text-xs text-muted-foreground">%</span>
                      </div>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      rentThreshold <= 15 ? 'bg-red-100 text-red-700' :
                      rentThreshold <= 25 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                    }`}>
                      Schwelle: {rentThreshold}%
                    </span>
                    <button onClick={fetchRentabilitaet}
                      className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                      <RefreshCw className={`h-3.5 w-3.5 text-muted-foreground ${rentLoading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  Lieferungsmenge vs. Abschriften (nur Shop-Artikel mit EAN). Artikel rot = Verlustquote über {rentThreshold}%.
                </p>
              </CardContent>
            </Card>

            {rentLoading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="h-10 w-10 animate-spin text-violet-600 mb-3" />
                <p className="text-sm text-muted-foreground">Lieferungs- und Abschriftsdaten werden verglichen…</p>
              </div>
            ) : rentData ? (
              <>
                {/* Summary Banner */}
                {rentData.summary.items_above_threshold > 0 && (
                  <div className="flex items-center gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-red-800 dark:text-red-300">
                        {rentData.summary.items_above_threshold} Artikel über {rentThreshold}% Verlust
                      </p>
                      <p className="text-xs text-red-700 dark:text-red-400">
                        Diese Artikel kosten mehr als sie einbringen. Sortiment überdenken!
                      </p>
                    </div>
                  </div>
                )}
                {rentData.summary.items_above_threshold === 0 && rentData.summary.total_shop_items > 0 && (
                  <div className="flex items-center gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-xl p-3">
                    <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                    <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                      Sehr gut! Kein Artikel über {rentThreshold}% Verlustquote.
                    </p>
                  </div>
                )}

                {/* Shop-Artikel Tabelle */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3 gap-3">
                      <SectionHeader
                        title={`Rentabilität: Shop-Artikel (${rentData.summary.total_shop_items} Artikel)`}
                        icon={Truck} color="#7c3aed"
                      />
                      {/* Suchleiste */}
                      <div className="relative flex-1 max-w-xs">
                        <input
                          type="text"
                          value={rentSearch}
                          onChange={e => setRentSearch(e.target.value)}
                          placeholder="Suche: Name, EAN, Art.-Nr."
                          className="w-full h-8 pl-8 pr-3 rounded-lg border border-border bg-background text-xs focus:ring-2 focus:ring-violet-400 focus:border-violet-400 outline-none"
                          data-testid="rent-search-input"
                        />
                        <svg className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M16.65 16.65A7 7 0 1117 10a7 7 0 01-.35 6.65z" />
                        </svg>
                        {rentSearch && (
                          <button onClick={() => setRentSearch('')} className="absolute right-2 top-1.5 text-muted-foreground hover:text-foreground">
                            <XCircle className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">
                      Zeitraum: {rentDays > 0 ? `Letzte ${rentDays} Tage` : 'Eigener Zeitraum'} · 
                      Geliefert vs. Abgeschrieben via EAN-Abgleich · Tabak/Zigaretten ausgeblendet
                    </p>
                    {(() => {
                      const q = rentSearch.toLowerCase();
                      const filtered = rentData.shop_items.filter(item =>
                        !q ||
                        (item.article_name || '').toLowerCase().includes(q) ||
                        (item.ean || '').includes(q) ||
                        (item.fremd_article_number || '').toLowerCase().includes(q)
                      );
                      return filtered.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4 text-center">
                          {rentSearch ? `Keine Artikel für "${rentSearch}"` : 'Keine Daten – entweder noch keine Lieferungen mit EAN oder keine MHD-Abschriften im Zeitraum.'}
                        </p>
                      ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="bg-violet-600 text-white">
                              <th className="text-left p-2.5 font-semibold rounded-tl-lg">Artikel</th>
                              <th className="text-left p-2.5 font-semibold">Art.-Nr.</th>
                              <th className="text-center p-2.5 font-semibold">EAN</th>
                              <th className="text-right p-2.5 font-semibold">Geliefert</th>
                              <th className="text-right p-2.5 font-semibold">Abgeschrieben</th>
                              <th className="text-center p-2.5 font-semibold rounded-tr-lg">Verlust</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filtered.map((item, i) => {
                              const isCritical = item.is_critical;
                              const isWarn = !isCritical && item.loss_rate >= rentThreshold * 0.7;
                              return (
                                <tr key={item.ean || i}
                                  className={`border-b ${
                                    isCritical ? 'bg-red-50 dark:bg-red-900/10' :
                                    isWarn ? 'bg-amber-50 dark:bg-amber-900/10' :
                                    i % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                                  }`}
                                  data-testid={`rent-item-${item.ean}`}
                                >
                                  <td className="p-2.5 font-medium max-w-[180px]">
                                    <div className="truncate">{item.article_name}</div>
                                    {item.has_no_delivery && (
                                      <span className="text-xs text-amber-600 font-normal">(keine Lieferung im Zeitraum)</span>
                                    )}
                                  </td>
                                  <td className="p-2.5 text-muted-foreground font-mono text-xs">
                                    {item.fremd_article_number || <span className="text-muted-foreground/40">–</span>}
                                  </td>
                                  <td className="p-2.5 text-center text-muted-foreground font-mono text-xs">
                                    {item.ean}
                                  </td>
                                  <td className="p-2.5 text-right font-semibold">
                                    {item.delivered > 0 ? item.delivered : <span className="text-muted-foreground">–</span>}
                                  </td>
                                  <td className="p-2.5 text-right">
                                    {item.written_off > 0
                                      ? <span className={isCritical ? 'text-red-600 font-bold' : 'text-amber-600'}>{item.written_off}</span>
                                      : <span className="text-green-600">0</span>
                                    }
                                  </td>
                                  <td className="p-2.5 text-center">
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                                      isCritical ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                                      isWarn ? 'bg-amber-100 text-amber-700' :
                                      item.loss_rate === 0 ? 'bg-green-100 text-green-700' : 'bg-blue-50 text-blue-700'
                                    }`}>
                                      {isCritical ? <XCircle className="h-3 w-3" /> :
                                       item.loss_rate === 0 ? <CheckCircle2 className="h-3 w-3" /> : null}
                                      {item.loss_rate}%
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {filtered.length < rentData.shop_items.length && (
                          <p className="text-xs text-muted-foreground mt-2 text-center">
                            {filtered.length} von {rentData.shop_items.length} Artikeln angezeigt
                          </p>
                        )}
                      </div>
                      );
                    })()}
                  </CardContent>
                </Card>

                {/* Artikel ohne EAN */}
                {rentData.no_ean_items.length > 0 && (
                  <Card>
                    <CardContent className="p-4">
                      <SectionHeader
                        title={`Artikel ohne EAN (${rentData.no_ean_items.length} – kein Rentabilitäts-Match möglich)`}
                        icon={AlertTriangle} color="#d97706"
                      />
                      <p className="text-xs text-muted-foreground mb-3">
                        EAN eintragen, damit diese Artikel künftig in der Rentabilitäts-Analyse erscheinen. Tabak/Zigaretten werden nicht angezeigt.
                      </p>
                      <div className="space-y-2">
                        {rentData.no_ean_items
                          .filter(item => {
                            if (!rentSearch) return true;
                            const q = rentSearch.toLowerCase();
                            return (item.article_name || '').toLowerCase().includes(q) ||
                                   (item.fremd_article_number || '').toLowerCase().includes(q);
                          })
                          .map((item) => {
                          const key = item.fremd_article_number || item.article_name;
                          const isSaving = savingEan[key];
                          return (
                            <div key={key}
                              className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg"
                              data-testid={`no-ean-item-${key}`}
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium truncate">{item.article_name}</p>
                                <p className="text-xs text-muted-foreground">
                                  Art.-Nr.: {item.fremd_article_number || '–'} · {item.quantity} Stk. geliefert
                                </p>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <Input
                                  value={eanInputs[key] || ''}
                                  onChange={e => setEanInputs(prev => ({ ...prev, [key]: e.target.value }))}
                                  placeholder="EAN (8-14 Ziffern)"
                                  className="h-7 text-xs w-36"
                                  data-testid={`ean-input-${key}`}
                                />
                                <Button
                                  size="sm"
                                  onClick={() => handleSaveEan(item)}
                                  disabled={isSaving || !eanInputs[key]}
                                  className="h-7 text-xs px-2 bg-amber-600 hover:bg-amber-700 gap-1"
                                  data-testid={`save-ean-btn-${key}`}
                                >
                                  {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <PenLine className="h-3 w-3" />}
                                  Speichern
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Backshop Abschriften (reine Menge) */}
                <Card>
                  <CardContent className="p-4">
                    <button
                      onClick={() => setShowBackshopSection(v => !v)}
                      className="flex items-center justify-between w-full"
                      data-testid="toggle-backshop-rent"
                    >
                      <SectionHeader
                        title={`Backshop-Abschriften (${rentData.backshop_items.length} Artikel)`}
                        icon={Package} color="#f97316"
                      />
                      <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showBackshopSection ? 'rotate-180' : ''}`} />
                    </button>
                    {showBackshopSection && (
                      <>
                        <p className="text-xs text-muted-foreground mb-3 mt-1">
                          Reine Abschriften-Menge (kein Lieferungs-Match da intern aufgebacken).
                        </p>
                        {rentData.backshop_items.length === 0 ? (
                          <p className="text-sm text-muted-foreground py-4 text-center">Keine Backshop-Abschriften im Zeitraum</p>
                        ) : (
                          <div className="space-y-1.5">
                            {rentData.backshop_items.map((item, i) => (
                              <div key={item.name} className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
                                <span className="text-xs text-muted-foreground w-5 text-center">{i + 1}</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium truncate">{item.name}</p>
                                  <p className="text-xs text-muted-foreground">{item.category}</p>
                                </div>
                                <span className="text-sm font-bold text-orange-600">{item.quantity} Stk.</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-violet-600 mb-3" />
                <p className="text-sm text-muted-foreground">Wird geladen…</p>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Statistik;
